import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, ButtonGroup, Field, Input, Modal } from "../../components/ui";
import { baseSymbol } from "../../core/currency";
import { useRepTarget, useStoreTarget } from "../../hooks/dailyRecap/useSalesTargets";

/**
 * Monthly turnover + profit targets — the inputs that used to sit on Analytics ›
 * Store Performance / Sales Rep (StoreTargetCard / RepTargetCard) and feed the
 * Daily Trading Report. Same hooks, same endpoints, pence on the wire.
 * kind="rep" → /api/users/{id}/monthly-turnover-target
 * kind="store" → /api/stores/{id}/monthly-targets (store-scoped; the admin/... twins stay master-only)
 */
function TargetModal({ kind, id, open, onClose }) {
  const qc = useQueryClient();
  const rep = useRepTarget(kind === "rep" && open ? id : null);
  const store = useStoreTarget(kind === "store" && open ? id : null);
  const t = kind === "rep" ? rep : store;
  const [turnover, setTurnover] = useState("");
  const [profit, setProfit] = useState("");

  useEffect(() => {
    const d = t.target || {};
    const tp = kind === "rep" ? d.month_turnover_target_pence : d.turnover_target_pence;
    const pp = kind === "rep" ? d.month_profit_target_pence : d.profit_target_pence;
    setTurnover(tp ? String(tp / 100) : "");
    setProfit(pp ? String(pp / 100) : "");
  }, [t.target, kind]);

  const save = () => {
    const toPence = (v) => Math.round(Number(v || 0) * 100);
    const payload =
      kind === "rep"
        ? { month_turnover_target_pence: toPence(turnover), month_profit_target_pence: toPence(profit) }
        : { turnover_target_pence: toPence(turnover), profit_target_pence: toPence(profit) };
    t.save(payload, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["dashboard"] });
        onClose();
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={kind === "rep" ? "Monthly target" : "Store monthly target"}
      description="Feeds the goal tracker and the Daily Trading Report."
      footer={
        <ButtonGroup align="end">
          <Button variant="secondary" data-btn="dashboard-target-cancel" onClick={onClose}>Cancel</Button>
          <Button variant="primary" data-btn="dashboard-target-save" loading={t.isSaving} disabled={t.isLoading} onClick={save}>Save</Button>
        </ButtonGroup>
      }
    >
      <div className="dash-form">
        <Field label={`Turnover (${baseSymbol()})`}>
          <Input type="number" min="0" size="md" fullWidth value={turnover} onChange={(e) => setTurnover(e.target.value)} placeholder="e.g. 300000" />
        </Field>
        <Field label={`Profit (${baseSymbol()})`}>
          <Input type="number" min="0" size="md" fullWidth value={profit} onChange={(e) => setProfit(e.target.value)} placeholder="e.g. 45000" />
        </Field>
      </div>
    </Modal>
  );
}

export default function TargetButton({ kind, id, label = "Set target" }) {
  const [open, setOpen] = useState(false);
  if (!id) return null;
  return (
    <>
      <Button variant="ghost" size="sm" data-btn="dashboard-target-open" onClick={() => setOpen(true)}>{label}</Button>
      {open ? <TargetModal kind={kind} id={id} open={open} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
